<template>
  <div v-if="stripePublicKey" class="page-buttons">
    <validation-summary :errors="errors"></validation-summary>
    <div class="row">
      <v-form-group class="col-12" :validator="$v.numberCompleted" label="Card Number">
        <card-number class="form-control"
                     v-bind:class="{ 'is-invalid': numberCompleted === false }"
                     ref="cardNumber"
                     :stripe="stripePublicKey"
                     :options="elementOptions"
                     @change="numberCompleted = $event.complete" />
      </v-form-group>

      <v-form-group class="col-6" :validator="$v.expiryCompleted" label="Expiry">
        <card-expiry class="form-control"
                     v-bind:class="{ 'is-invalid': expiryCompleted === false }"
                     ref="cardExpiry"
                     :stripe="stripePublicKey"
                     :options="elementOptions"
                     @change="expiryCompleted = $event.complete" />
      </v-form-group>

      <v-form-group class="col-6" :validator="$v.cvcCompleted" label="CVC">
        <card-cvc class="form-control"
                  v-bind:class="{ 'is-invalid': cvcCompleted === false }"
                  ref="cardCvc"
                  :stripe="stripePublicKey"
                  :options="elementOptions"
                  @change="cvcCompleted = $event.complete" />
      </v-form-group>

      <v-form-group class="col-12" :validator="$v.cardholderName" label="Cardholder name">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="cardholderName"
                      placeholder="Cardholder name" />
      </v-form-group>

      <v-form-group class="col-12" :validator="$v.address1" label="Address">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="address1"
                      placeholder="Address" />
      </v-form-group>

      <v-form-group class="col-12" :validator="$v.city" label="City">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="city"
                      placeholder="City" />
      </v-form-group>

      <v-form-group class="col-6" :validator="$v.state" label="State">
        <v-select slot-scope="{ attrs, listeners }"
                  v-bind="attrs"
                  v-on="listeners"
                  :options="usaStates.map(state => ({ label: state.name, value: state.name}))"
                  :clearable="false"
                  :filterable="false"
                  :searchable="false"
                  placeholder="State"
                  :reduce="option => option.value"
                  v-model="state" />
      </v-form-group>

      <v-form-group class="col-6" :validator="$v.zip" label="ZIP">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="zip"
                      placeholder="ZIP" />
      </v-form-group>

      <v-form-group class="col-12" label="Coupon code">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="coupon.code"
                      placeholder="Coupon code"
                      @change="couponCodeChanged" />
      </v-form-group>
      <div class="col-12 text-danger" v-if="coupon.error">{{ coupon.error }}</div>
    </div>
    <div class="float-right mt-2">
      <div class="h6 font-weight-normal">Subtotal: <strong>${{ survey.reportInvoice.amount.toFixed(2) }}</strong></div>
      <div class="h6 font-weight-normal" v-if="coupon.id">Coupon discount: <strong>${{ couponDiscount.toFixed(2) }}</strong></div>
      <div class="h6 font-weight-bold">Total: <strong>${{ total.toFixed(2) }}</strong></div>
    </div>
    <b-form-checkbox v-model="needSaveCard" v-if="false">Save card?</b-form-checkbox>
  </div>
</template>

<script>
  import formMixin from "mixins/form-mixin";
  import { required } from "vuelidate/lib/validators";
  import { requiredStripeElement } from "utils/custom-validators";

  import round from "lodash/round";

  import { mapState, mapActions } from 'vuex';

  import { CardNumber, CardExpiry, CardCvc, createToken } from 'vue-stripe-elements-plus';

  import { usaStates } from 'typed-usa-states';

  import couponService from 'services/coupon-service';
  import couponType from 'enums/coupon-type';

  const defaultCoupon = {
    code: null,
    value: 0,
    type: couponType.Amount,
    error: null
  };

  export default {
    mixins: [formMixin],

    data () {
      return {
        numberCompleted: null,
        expiryCompleted: null,
        cvcCompleted: null,
        needSaveCard: false,

        cardholderName: null,
        cardholderEmail: null,
        address1: null,
        city: null,
        state: null,
        zip: null,

        coupon: { ...defaultCoupon },

        usaStates,
        stripePublicKey: null,
        round,

        elementOptions: {
          elements: {
            fonts: [
              {
                cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat&display=swap'
              }
            ]
          },
          style: {
            base: {
              fontWeight: 400,
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '1rem',
              color: '#333',
              '::placeholder': {
                color: '#999999',
              },
            },
            invalid: {
              color: '#333',
            },
          },
        }
      }
    },

    components: { CardNumber, CardExpiry, CardCvc },

    validations: {
      numberCompleted: {
        requiredStripeElement
      },
      cvcCompleted: {
        requiredStripeElement
      },
      expiryCompleted: {
        requiredStripeElement
      },
      cardholderName: {
        required
      },
      cardholderEmail: {
        required
      },
      address1: {
        required
      },
      city: {
        required
      },
      state: {
        required
      },
      zip: {
        required
      }
    },

    computed: {
      ...mapState("survey", ["survey"]),
      ...mapState("user", ["user"]),

      couponDiscount() {
        return round(this.coupon.type == couponType.Amount
          ? this.coupon.value
          : this.survey.reportInvoice.amount * this.coupon.value / 100, 2);
      },

      total() {
        return round(this.couponDiscount >= this.survey.reportInvoice.amount
          ? 0
          : this.survey.reportInvoice.amount - this.couponDiscount, 2);
      }
    },

    async created() {
      this.cardholderName = `${this.user.firstName} ${this.user.lastName}`;
      this.cardholderEmail = this.user.email;
      this.stripePublicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;

      if (this.user.address) {
        this.address1 = this.user.address.address1 || "";
        this.city = this.user.address.city || "";
        this.state = this.user.address.state || "";
        this.zip = this.user.address.zip || "";
      }
    },

    methods: {
      ...mapActions("survey", ["charge"]),

      async onSubmit() {
        const result = await createToken();

        if (!result || !result.token)
          return false;

        const chargeData = {
          needSaveCard: this.needSaveCard,
          token: result.token.id,
          couponId: this.coupon?.id || null,

          customer: {
            cardholderName: this.cardholderName,
            cardholderEmail: this.cardholderEmail,
            address1: this.address1,
            state: this.state,
            city: this.city,
            zip: this.zip
          }
        };

        await this.charge(chargeData);
      },

      async couponCodeChanged() {
        try {
          this.coupon = await couponService.getByCode(this.coupon.code);
        } catch (error) {
          this.coupon = { ...defaultCoupon, error: error.customErrors[0] };
        }
      }
    }
  }
</script>